import AHDate from "_shared/dates/date";
import type WorkAgreement from "work/work-agreements/work-agreement";

/** The two version numbers a work agreement or proposal read carries (#6636). */
export type WorkVersionNumbers = Readonly<{
  /** The version of the work the record is, once it is hired. */
  versionNumber?: number | null;
  /** The version the record becomes when it is hired. */
  proposedVersionNumber?: number | null;
}>;

/**
 * Which version of the work a record is, so records that share a project name can be told apart
 * (#6636). The first hired agreement is v1, and each hired change order is the next. A proposal, and
 * an agreement not yet hired, carries the version it creates at hire: "v3 (proposed)".
 *
 * Every surface that names a work agreement or a proposal shows this beside the name, in the words
 * #6636 set: `{Project name} · v{n}`, "v{n} (proposed)", and "superseded on {date}" for a version a
 * later one replaced. Read it with `WorkVersion.of`, never by formatting the numbers again.
 */
export default class WorkVersion {
  /** The separator between a project name and its version, and between the version and its state. */
  public static readonly SEPARATOR = " · ";

  /**
   * The version `agreement` is, or undefined when the payload carries none: one older than the
   * numbers, where a guessed "v1" would be wrong for every change order.
   *
   * A proposal's version is its work agreement's: pass `proposal.workAgreement`.
   */
  public static of(agreement: WorkAgreement | null | undefined): WorkVersion | undefined {
    if (!agreement) return undefined;
    const number = agreement.versionNumber ?? agreement.proposedVersionNumber;
    if (number === undefined) return undefined;
    // A hired agreement whose own number is missing still is the version it proposed.
    const isProposed = agreement.versionNumber === undefined && !agreement.isHired;
    if (!agreement.isReplaced) return new WorkVersion(number, isProposed);
    // The hire that replaced an agreement archived it at that moment, so the archive date is when it
    // was superseded. One the team-quit workflow had already ended keeps the date the quit set.
    return new WorkVersion(number, isProposed, true, agreement.archiveDate ?? agreement.endDate ?? undefined);
  }

  /**
   * The current, hired version `number`, for a row that carries only the number -- a team on
   * running work, say. Undefined when there is none.
   */
  public static hired(number: number | null | undefined): WorkVersion | undefined {
    return number ? new WorkVersion(number, false) : undefined;
  }

  /**
   * The version a read carries only the numbers of -- a by-ids read behind a conversation's
   * subtitle or a notification row (#6809): hired once it has its own number, else proposed.
   * Undefined when it carries neither.
   */
  public static fromNumbers(record: WorkVersionNumbers | null | undefined): WorkVersion | undefined {
    if (record?.versionNumber) return new WorkVersion(record.versionNumber, false);
    if (record?.proposedVersionNumber) return new WorkVersion(record.proposedVersionNumber, true);
    return undefined;
  }

  /**
   * The version a conversation filed under `workAgreement` is about now. A work keeps its
   * conversations under its first agreement, so they name the work's latest hire -- v2 once v2 is
   * hired -- and fall back to the agreement's own numbers before any hire (#6809).
   */
  public static ofConversation(
    workAgreement: (WorkVersionNumbers & { currentVersionNumber?: number | null }) | null | undefined,
  ): WorkVersion | undefined {
    if (workAgreement?.currentVersionNumber) return new WorkVersion(workAgreement.currentVersionNumber, false);
    return WorkVersion.fromNumbers(workAgreement);
  }

  /** `nameWith` when `version` is known, else the name alone. */
  public static nameWithVersion(name: string, version: WorkVersion | undefined): string {
    return version ? version.nameWith(name) : name;
  }

  /**
   * "v2" for a hired version, "v3 (proposed)" for one a proposal would create. The short form, for
   * a chip beside a name.
   */
  public get label(): string {
    return this.isProposed ? `v${this.number} (proposed)` : `v${this.number}`;
  }

  /** "superseded on 10/13/2026" for a version a later one replaced, else undefined. */
  public get supersededText(): string | undefined {
    if (!this.isSuperseded) return undefined;
    return this.supersededOn ? `superseded on ${this.supersededOn.toString()}` : "superseded";
  }

  /** The label with the superseded state when there is one: "v1 · superseded on 10/13/2026". */
  public get description(): string {
    const superseded = this.supersededText;
    return superseded ? `${this.label}${WorkVersion.SEPARATOR}${superseded}` : this.label;
  }

  private constructor(
    public readonly number: number,
    public readonly isProposed: boolean,
    public readonly isSuperseded = false,
    public readonly supersededOn?: AHDate,
  ) {}

  /**
   * The name a reader can tell apart from every other version of the work: "10/13 Test · v2". For a
   * page title, a breadcrumb, a heading's accessible name, or any text that names the record.
   */
  public nameWith(name: string): string {
    return `${name}${WorkVersion.SEPARATOR}${this.description}`;
  }
}
